import React, { useState, useEffect } from 'react';
import { ListGroup, Button, Form, InputGroup, Spinner, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSearch, faComments } from '@fortawesome/free-solid-svg-icons';
import ConversationItem from './ConversationItem';
import { getConversations } from '../../services/messagingService';
import './ConversationList.css';

function ConversationList({ selectedThreadId, onSelectThread, onNewMessage }) {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    loadConversations();
  }, []);

  const loadConversations = async (pageNum = 1, append = false) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);
      
      const response = await getConversations({ page: pageNum, limit: 20 });
      
      // Handle both array response and paginated response
      const data = Array.isArray(response) ? response : response.conversations || [];
      const pagination = response.pagination || {};
      
      if (append) {
        setConversations(prev => [...prev, ...data]);
      } else {
        setConversations(data);
      }
      
      setHasMore(pagination.hasNext || false);
      setPage(pageNum);
    } catch (err) {
      setError('Failed to load conversations');
      console.error('Error loading conversations:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreConversations = () => {
    if (!loadingMore && hasMore) {
      loadConversations(page + 1, true);
    }
  };

  const filteredConversations = conversations.filter(conv => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      conv.participantNames?.some(name => name.toLowerCase().includes(query)) ||
      conv.lastMessage?.content?.toLowerCase().includes(query)
    );
  });

  const unreadCount = conversations.filter(conv => conv.unreadCount > 0).length;

  return (
    <div className="conversation-list">
      <div className="conversation-list-header">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">
            <FontAwesomeIcon icon={faComments} className="me-2" />
            Messages
            {unreadCount > 0 && (
              <Badge bg="primary" className="ms-2">{unreadCount}</Badge>
            )}
          </h5>
          <Button
            variant="primary"
            size="sm"
            onClick={onNewMessage}
            className="new-message-btn"
          >
            <FontAwesomeIcon icon={faPlus} className="me-1" />
            New
          </Button>
        </div>

        <InputGroup className="mb-3">
          <InputGroup.Text>
            <FontAwesomeIcon icon={faSearch} />
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </InputGroup>
      </div>

      <div className="conversation-list-body">
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted">Loading conversations...</p>
          </div>
        ) : error ? (
          <div className="text-center py-5 text-danger">
            <p>{error}</p>
            <Button variant="link" onClick={loadConversations}>
              Try again
            </Button>
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <FontAwesomeIcon icon={faComments} size="3x" className="mb-3 opacity-25" />
            <p>
              {searchQuery
                ? 'No conversations match your search'
                : 'No conversations yet'}
            </p>
            {!searchQuery && (
              <Button variant="primary" size="sm" onClick={onNewMessage}>
                Start a conversation
              </Button>
            )}
          </div>
        ) : (
          <ListGroup variant="flush">
            {filteredConversations.map((conversation) => (
              <ConversationItem
                key={conversation.threadId}
                conversation={conversation}
                isSelected={conversation.threadId === selectedThreadId}
                onClick={() => onSelectThread(conversation)}
              />
            ))}
          </ListGroup>
        )}
        
        {/* Load more button */}
        {!loading && !searchQuery && hasMore && (
          <div className="text-center py-3">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={loadMoreConversations}
              disabled={loadingMore}
            >
              {loadingMore ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading...
                </>
              ) : (
                'Load More'
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ConversationList;
